import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { LoginContext } from "../context/LoginContext";
import {
  LogOut,
  Github,
  Facebook,
  Twitter,
  Linkedin,
  Youtube,
  User as UserIcon,
  CheckCircle2,
  Download,
} from "lucide-react";
import apiConfig from "../config/apiConfig";
import { logout } from "../apis/backend";
import iiitblogo from "../images/iiitb_logo.png";
import "./StudentHome.css";
import LogoutButton from "./LogoutButton";
import HelpIcon from "../images/help_chat.png";
import HelpQueryPopup from "./QueryManager/HelpQuery/HelpQueryPopup";
import MyQueriesPopup from "./QueryManager/MyQuery/MyQueriesPopup";
import QueryChatPopup, {
  help_desk_query,
} from "./QueryManager/QueryChat/QueryChatPopup";
import AllQueriesPopup from "./QueryManager/AllQueries/AllQueriesPopup";
import { toast } from "../utils/toast";
import { apiFetch } from "../apis/apiFetch";
import PaymentIciciInitiate from "./PaymentIciciInitiate";
import { DriveContext } from "../context/DriveContext";

const StudentHome = () => {
  const navigate = useNavigate();
  const { user } = useContext(LoginContext);
  const { setIsAppEdit, setIsDriveEditOpen, setIsDriveOpen, setIsJeeUpdate, isAppEdit, isDriveEdit, isDriveOpen: driveOpen } = useContext(DriveContext);
  const [drives, setDrives] = useState<any[]>([]);
  const [rounds, setRounds] = useState<any[]>([]);
  const [applications, setApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [serverTimeOffset, setServerTimeOffset] = useState<number>(0);

  // Use user info from context, with fallbacks
  const userEmail = user?.email_id || "Student";
  const userId = user?.userId || "";

  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedDriveToApply, setSelectedDriveToApply] = useState<{ id: string, name: string } | null>(null);
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [showMyQueriesPopup, setShowMyQueriesPopup] = useState(false);
  const [showQueryChatPopup, setShowQueryChatPopup] = useState(false);
  const [showAllQueriesPopup, setShowAllQueriesPopup] = useState(false);
  const [selectedQueryId, setSelectedQueryId] = useState<string>("");
  const [selectedQuerySubject, setSelectedQuerySubject] = useState<string>("");
  const [selectedQuery, setSelectedQuery] = useState<help_desk_query | null>(
    null,
  );
  const [downloadingApp, setDownloadingApp] = useState<string | null>(null);
  const [showAcceptOfferPopup, setShowAcceptOfferPopup] = useState(false);
  const [selectedAppToAccept, setSelectedAppToAccept] = useState<any>(null);
  // For multi-offer: tracks which specific pending offer the student is acting on
  const [selectedOfferToAction, setSelectedOfferToAction] = useState<any>(null);
  const [offerActionConfirm, setOfferActionConfirm] = useState<'ACCEPT' | 'REJECT' | 'ACCEPT_CONFIRM' | null>(null);
  const [showWithdrawConfirmPopup, setShowWithdrawConfirmPopup] = useState(false);
  const [selectedAppToWithdraw, setSelectedAppToWithdraw] = useState<any>(null);

  const [showOnlineRegPopup, setShowOnlineRegPopup] = useState(false);
  const [onlineRegAction, setOnlineRegAction] = useState<'REGISTER' | 'DECLINE' | null>(null);
  const [onlineRegApp, setOnlineRegApp] = useState<any>(null);

  useEffect(() => {
    localStorage.removeItem("activeDriveId"); // Clear stale drive context on home
    const fetchDrivesAndApplications = async () => {
      try {
        // 1. Fetch Drives
        // apiFetch will handle the token refresh automatically if Cookies.get("access_token") is null
        const driveResponse = await apiFetch(
          `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`
        );

        if (driveResponse.ok) {
          // Sync with server time to handle incorrect local system clocks
          let serverDateHeader = driveResponse.headers.get("Date");

          // CORS.
          // Fall back to making a HEAD request to our own frontend origin.
          if (!serverDateHeader) {
            try {
              const headRes = await fetch("/config.json", { method: "HEAD", cache: "no-store" });
              serverDateHeader = headRes.headers.get("Date");
            } catch (e) {
              console.warn("Could not fetch server time fallback", e);
            }
          }

          if (serverDateHeader) {
            const serverTime = new Date(serverDateHeader).getTime();
            const localTime = new Date().getTime();
            setServerTimeOffset(serverTime - localTime);
          }

          const data = await driveResponse.json();
          setDrives(data.resource || []);
        }

        const roundsResponse = await apiFetch(
          `${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`
        );

        if (roundsResponse.ok) {

          const data = await roundsResponse.json();
          setRounds(data.resource || []);
        }

        // 2. Fetch Applications from application_track resource
        if (userId) {
          const appResponse = await apiFetch(
            `${apiConfig.getResourceUrl('applications')}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${userId}`
          );

          if (appResponse.ok) {
            const appData = await appResponse.json();
            setApplications(appData.resource || []);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDrivesAndApplications();
  }, [userId]); // Re-fetch if userId becomes available (e.g. context load delay)



  const base64EncodeFun = (str: string) => {
    return btoa(unescape(encodeURIComponent(str)));
  };

  useEffect(() => {
    setIsAppEdit(false)
    setIsDriveEditOpen(false)
    setIsDriveOpen(false)
    setIsJeeUpdate(false)
  }, [])

  const handleApply = async (driveId: string, driveName: string, isAppEdit: boolean, isLocked: boolean) => {
    // Block navigation if drive is closed

    const drive = drives.find((d: any) => d.id === driveId);
    if (drive && !isDriveOpen(drive.enddate) && !isDriveEditOpen(drive.editstartdate, drive.editenddate) && !isAppEdit) {
      return;
    }

    const driveOpenNow = isDriveOpen(drive.enddate);
    const driveEditNow = isDriveEditOpen(drive.editstartdate, drive.editenddate);

    setIsAppEdit(isAppEdit)
    setIsDriveOpen(driveOpenNow);
    setIsDriveEditOpen(driveEditNow);

    // Block navigation if drive is closed and not editable
    if (!driveOpenNow && !driveEditNow && !isAppEdit) {
      return;
    }


    const isDriveEdit = drive?.drivetype === 'Btech' && isDriveEditOpen(drive.editstartdate, drive.editenddate)

    // Check if application already exists for this drive (ignoring zombie records)
    const existingApp = applications.find((app: any) => {
      if (app.drive_id !== driveId) return false;
      return !(app.payment_status === "FAILED" && !app.fullname);
    });

    const isBtechApp = drive.drivetype === "Btech"

    if (isBtechApp && existingApp && isAppEdit && !isLocked) {
      // Application exists — navigate to edit
      localStorage.setItem("activeDriveId", driveId);
      navigate("/personaldetails");
      return;
    }
    if (isBtechApp && existingApp && isDriveEdit && !isAppEdit && isLocked) {
      // Application exists — navigate to edit
      localStorage.setItem("activeDriveId", driveId);
      navigate("/examdetails");
      return;
    }
    if (existingApp) {
      // Application exists — navigate to edit
      localStorage.setItem("activeDriveId", driveId);
      navigate("/personaldetails");
      return;
    }

    // Application number is generated server-side in the decorator
    const applicationNumber = "PENDING";

    try {
      const formData = new FormData();
      const jsonString = JSON.stringify({ application_number: applicationNumber, user_id: userId, drive_id: driveId, status: "DRAFT" })
      const base64Encoded = base64EncodeFun(jsonString);
      formData.append('resource', base64Encoded);
      formData.append('queryId', 'CREATE');
      const response = await apiFetch(
        `${apiConfig.getResourceUrl('applications')}`,
        {
          method: "POST",
          body: formData,
        }
      );

      const resJson = await response.json();
      if (resJson.errCode === -1) {
        toast.error("Error: " + resJson.message);
        return;
      }

      // Update local state with the new application
      setApplications((prev) => [
        ...prev,
        resJson.resource ? resJson.resource : resJson,
      ]);

      localStorage.setItem("activeDriveId", driveId);
      navigate("/personaldetails");
    } catch (error) {
      console.error("Error creating application:", error);
      toast.error("Failed to create application. Please try again.");
    }
  };

  const parseSafeDate = (dateStr: string) => {
    if (!dateStr) return new Date(NaN);
    // Replace Narrow No-Break Space (U+202F) and Non-Breaking Space (U+00A0) with regular space.
    // Modern Java (JDK 17+) injects U+202F before AM/PM, which crashes older JS engines.
    let cleaned = dateStr.replace(/[\u202F\u00A0]/g, ' ');
    // Remove trailing commas and extra spaces that trip up old browsers
    cleaned = cleaned.replace(/,\s*$/, "").trim();
    return new Date(cleaned);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const d = parseSafeDate(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const isDriveOpen = (endDateStr: string) => {
    if (!endDateStr) return true; // Assume open if no end date
    const endDate = parseSafeDate(endDateStr);
    if (isNaN(endDate.getTime())) return false; // Safety fallback
    const today = new Date(Date.now() + serverTimeOffset);
    today.setHours(0, 0, 0, 0);
    return today <= endDate;
  };

  const isDriveEditOpen = (startDateStr: string, endDateStr: string) => {
    if (!startDateStr) return false;
    if (!endDateStr) return false;
    const startDate = parseSafeDate(startDateStr);
    const endDate = parseSafeDate(endDateStr);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return false;
    const today = new Date(Date.now() + serverTimeOffset);
    today.setHours(0, 0, 0, 0);
    return today >= startDate && today <= endDate;
  };

  const isEditingAllowed = (alloweditingenddateStr: string) => {
    if (!alloweditingenddateStr) return true; // Assume open if no alloweditingenddate
    const endDate = parseSafeDate(alloweditingenddateStr);
    if (isNaN(endDate.getTime())) return false; // Safety fallback
    const today = new Date(Date.now() + serverTimeOffset);
    today.setHours(0, 0, 0, 0);
    return today <= endDate;
  };

  const isActionAllowed = (roundName: string, dateField: 'enddate' | 'withdrawal_end_date') => {
    if (!roundName) return true;
    const round = rounds.find(r => r.name === roundName);
    if (!round) return true;

    const dateStr = round[dateField];
    if (!dateStr) return true; // If not defined, default to allowing action

    const deadline = parseSafeDate(dateStr);
    if (isNaN(deadline.getTime())) return false;
    // Set to end of the day to allow actions on the deadline date
    deadline.setHours(23, 59, 59, 999);

    const serverTime = new Date(Date.now() + serverTimeOffset);
    return serverTime <= deadline;
  };

  const getStatusBadgeClass = (status: string, paymentStatus?: string) => {
    if (paymentStatus === "SUCCESS" || (status && status !== "DRAFT")) {
      return "status-submitted";
    }
    return "status-draft";
  };

  const getStatusLabel = (status: string, paymentStatus?: string) => {
    if (paymentStatus === "SUCCESS" || (status && status !== "DRAFT")) {
      return "Submitted";
    }
    return "Draft";
  };

  const getProgrammeName = (name: string) => {
    const lowerName = name.toLowerCase();
    if (lowerName.includes('b.tech') || (lowerName.includes('i.m-tech'))) {
      return "4-year Bachelor of Technology and 5-year Integrated Master of Technology programmes";
    }
    if (lowerName.includes('integrated m.tech') || lowerName.includes('i.m-tech') || lowerName.includes('imtech') || lowerName.includes('i.m.tech')) {
      return `5-year Integrated Master of Technology programme in ${name.replace(/Integrated M\.Tech|I\.M-Tech|IMTech|I\.M\.Tech/ig, '').replace(/[()-]/g, '').trim()}`;
    }
    if (lowerName.includes('m.tech')) {
      return `2-year Master of Technology programme in ${name.replace(/M\.Tech/ig, '').replace(/[()-]/g, '').trim()}`;
    }
    if (lowerName.includes('b.tech')) {
      return `4-year Bachelor of Technology programme in ${name.replace(/B\.Tech/ig, '').replace(/[()-]/g, '').trim()}`;
    }
    return `Programme in ${name}`;
  };

  const handleDownloadPDF = async (applicationNumber: string) => {
    try {
      setDownloadingApp(applicationNumber);

      const response = await apiFetch(
        `${apiConfig.API_BASE_URL}/payments/download-pdf/${applicationNumber}`,
        { method: "GET" }
      );

      if (!response.ok) {
        throw new Error("Failed to download PDF");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Application_${applicationNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("Failed to download application PDF.");
    } finally {
      setDownloadingApp(null);
    }
  };

  // Download an offer letter — accepts an optional specific offer from pending_offers[]
  // (each offer has its own doc_id when multiple branches were offered)
  const handleDownloadOfferLetter = async (app: any, offer?: any) => {
    try {
      const documentId = offer?.doc_id || app?.offer_letter_doc_id;
      if (!documentId) {
        toast.error("Offer letter not available");
        return;
      }

      const userId = user?.userId;
      const progSuffix = offer?.programme ? `_${offer.programme}` : "";

      const response = await apiFetch(
        `${apiConfig.getResourceUrl("message")}?document_id=${documentId}&queryId=GET_DOCUMENT&dmsRole=admin&user_id=${userId}`,
        { method: "GET" }
      );

      if (!response.ok) throw new Error("Failed to fetch offer letter");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Offer_Letter_${app.application_number}${progSuffix}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Offer letter download failed:", err);
      toast.error("Failed to download offer letter.");
    }
  };

  const handleUpdateAppStatus = async (app: any, newStatus: string, successMsg: string) => {
    try {
      const payload = { ...app, offer_status: newStatus };
      const formData = new FormData();
      formData.append("resource", base64EncodeFun(JSON.stringify(payload)));
      formData.append("action", "MODIFY");

      const response = await apiFetch(apiConfig.getResourceUrl("applications"), {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        toast.success(successMsg);
        setApplications(prev => prev.map(a => a.id === app.id ? { ...a, offer_status: newStatus } : a));
      } else {
        toast.error("Failed to update status");
      }
    } catch {
      toast.error("Network error");
    }
  }


  // Reject: marks the selected slot as REJECTED.
  // If no more pending offers remain → set global offer_status = REJECTED.
  const handleRejectOffer = async (app: any) => {
    const offer = selectedOfferToAction;

    // Online round rejection — updates online_offer_status only
    if (offer?.isOnline) {
      const statusField = offer.isOnlineRound5 ? "online_round5_offer_status" : (offer.isOnlineRound4 ? "online_round4_offer_status" : (offer.isOnlineRound3 ? "online_round3_offer_status" : (offer.isOnlineRound2 ? "online_round2_offer_status" : "online_offer_status")));
      const payload = { ...app, [statusField]: "REJECTED", offer_status: "REJECTED" };
      try {
        const formData = new FormData();
        formData.append("resource", base64EncodeFun(JSON.stringify(payload)));
        formData.append("action", "MODIFY");
        const response = await apiFetch(apiConfig.getResourceUrl("applications"), {
          method: "POST", body: formData,
        });
        if (response.ok) {
          toast.success(`Offer for ${offer.programme.replace(/_/g, " ")} has been declined.`);
          setApplications(prev => prev.map(a =>
            a.id === app.id ? { ...a, [statusField]: "REJECTED", offer_status: "REJECTED" } : a
          ));
        } else {
          toast.error("Failed to update status");
        }
      } catch { toast.error("Network error"); }
      setShowAcceptOfferPopup(false);
      setSelectedOfferToAction(null);
      return;
    }

    const payload = { ...app };
    if (offer.slot === 1) payload.offer_status_1 = "REJECTED";
    if (offer.slot === 2) payload.offer_status_2 = "REJECTED";
    if (offer.slot === 3) payload.offer_status_3 = "REJECTED";

    // Check if any offers are still pending
    const stillPending = [
      payload.offer_status_1,
      payload.offer_status_2,
      payload.offer_status_3
    ].some(s => s === "OFFER_SENT");

    if (!stillPending) {
      payload.offer_status = "REJECTED";
    }

    try {
      const formData = new FormData();
      formData.append("resource", base64EncodeFun(JSON.stringify(payload)));
      formData.append("action", "MODIFY");
      const response = await apiFetch(apiConfig.getResourceUrl("applications"), {
        method: "POST", body: formData,
      });
      if (response.ok) {
        toast.success(`Offer for ${offer.programme.replace(/_/g, " ")} has been declined.`);
        setApplications(prev => prev.map(a =>
          a.id === app.id ? { ...a, ...payload } : a
        ));
      } else {
        toast.error("Failed to update status");
      }
    } catch { toast.error("Network error"); }
    setShowAcceptOfferPopup(false);
    setSelectedOfferToAction(null);
  };

  const handleAcceptOffer = async (app: any) => {
    const offer = selectedOfferToAction;
    if (!offer) return;

    const payload = { ...app };
    if (offer.slot === 1) payload.offer_status_1 = "ACCEPTED";
    if (offer.slot === 2) payload.offer_status_2 = "ACCEPTED";
    if (offer.slot === 3) payload.offer_status_3 = "ACCEPTED";

    // Auto-reject other pending or accepted offers
    if (offer.slot !== 1 && (payload.offer_status_1 === "OFFER_SENT" || payload.offer_status_1 === "ACCEPTED")) payload.offer_status_1 = "REJECTED";
    if (offer.slot !== 2 && (payload.offer_status_2 === "OFFER_SENT" || payload.offer_status_2 === "ACCEPTED")) payload.offer_status_2 = "REJECTED";
    if (offer.slot !== 3 && (payload.offer_status_3 === "OFFER_SENT" || payload.offer_status_3 === "ACCEPTED")) payload.offer_status_3 = "REJECTED";

    payload.offer_status = "ACCEPTED";

    try {
      const formData = new FormData();
      formData.append("resource", base64EncodeFun(JSON.stringify(payload)));
      formData.append("action", "MODIFY");
      const response = await apiFetch(apiConfig.getResourceUrl("applications"), {
        method: "POST", body: formData,
      });
      if (response.ok) {
        toast.success(`Offer for ${offer.programme.replace(/_/g, " ")} has been accepted.`);
        setApplications(prev => prev.map(a =>
          a.id === app.id ? { ...a, ...payload } : a
        ));
        setShowAcceptOfferPopup(false);
        setOfferActionConfirm(null);
        setSelectedOfferToAction(null);
      } else {
        toast.error("Failed to accept offer");
      }
    } catch {
      toast.error("Network error");
    }
  };

  const handleWithdrawOffer = async (app: any) => {
    setSelectedAppToWithdraw(app);
    setShowWithdrawConfirmPopup(true);
  };

  const handleOnlineRound = (app: any) => {
    navigate(`/onlineRound/${app?.id}`)
  }

  const handleJeeUpdate = (isJeeUpdateAllowed: boolean) => {
    if (isJeeUpdateAllowed) {
      setIsAppEdit(isJeeUpdateAllowed)
      navigate(`/jeeupdate`)
    } else {
      toast.error("JEE Update Not Allowed");
    }
  }

  const handleWithdrawal = (isJeeUpdateAllowed: boolean) => {
    if (isJeeUpdateAllowed) {
      navigate(`/withdrawal`)
    } else {
      toast.error("Withdrawal  Not Allowed");
    }
  }

  const getAcceptedProgrammeName = (app: any) => {
    if (!app) return "";
    const slots = [
      { p: app.offered_programme_1, s: app.offer_status_1 },
      { p: app.offered_programme_2, s: app.offer_status_2 },
      { p: app.offered_programme_3, s: app.offer_status_3 }
    ];
    const accepted = slots.find(o => o.s === "ACCEPTED");
    return (accepted?.p || app.offered_programme || "")?.replace(/_/g, " ");
  };

  const currentOnlineRoundId = selectedOfferToAction?.isOnlineRound5
    ? (selectedAppToAccept?.online_round5_offered_round_id || selectedAppToAccept?.online_round5_id || selectedAppToAccept?.onlineRound)
    : selectedOfferToAction?.isOnlineRound4
    ? (selectedAppToAccept?.online_round4_offered_round_id || selectedAppToAccept?.online_round4_id || selectedAppToAccept?.onlineRound)
    : selectedOfferToAction?.isOnlineRound3
    ? (selectedAppToAccept?.online_round3_offered_round_id || selectedAppToAccept?.online_round3_id || selectedAppToAccept?.onlineRound)
    : selectedOfferToAction?.isOnlineRound2
      ? (selectedAppToAccept?.online_round2_offered_round_id || selectedAppToAccept?.online_round2_id || selectedAppToAccept?.onlineRound)
      : selectedAppToAccept?.onlineRound;

  const roundFee = rounds.find((round) => round?.name === selectedAppToAccept?.offered_round_name)?.fee;
  const onlineRoundFee = rounds.find((round) => round?.id === currentOnlineRoundId)?.fee;
  const effectiveRoundFee = selectedOfferToAction?.isOnline ? onlineRoundFee : roundFee;
  const effectiveRoundName = selectedOfferToAction?.isOnline
    ? (rounds.find((r) => r.id === currentOnlineRoundId)?.name || "Online Round")
    : decodeURIComponent(selectedOfferToAction?.round_name || selectedAppToAccept?.offered_round_name || "").trim();


  return (
    <div className="student-home-container">
      {/* Header */}
      <header className="student-header">
        <div className="header-left">
          <img src={iiitblogo} alt="IIITB" className="header-logo" />
          <div className="header-title-group">
            <h1 className="header-inst-name">
              International Institute of Information Technology Bangalore
            </h1>
            <span className="header-portal-name">Admissions Portal</span>
          </div>
        </div>
        <div className="header-right">
          <div
            style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "8px" }}
            onClick={() => navigate("/newQuery")}
          >
            <img
              src={HelpIcon}
              className="help_icon"
              alt="help-icon"
            //   onClick={() => setShowHelpPopup(true)}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>Ask a Query</span>
              <span style={{ fontSize: "12px", color: "#d3cdcdff" }}>
                Submit your Question <br /> to the admission team
              </span>
            </div>
          </div>
          <div className="user-profile">
            <div className="user-avatar" title={userEmail}>
              <UserIcon size={20} />
            </div>
            <span className="user-email-text">{userEmail.split("@")[0]}</span>
          </div>
          <LogoutButton className="logout-btn" />
        </div>
      </header>

      {/* Main Content */}
      <div className="content-section">
        {loading ? (
          <div className="text-center p-5">Loading available drives...</div>
        ) : drives.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center mt-5 mb-5 mx-3" style={{ minHeight: "35vh" }}>
            <div style={{
              maxWidth: "760px",
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderTop: "6px solid #2D88D4",
              borderRadius: "12px",
              padding: "32px",
              boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.05)",
              color: "#1f2937",
              fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif",
              textAlign: "center"
            }}>
              <div className="d-flex flex-column align-items-center justify-content-center gap-3 mb-4">
                <div style={{
                  backgroundColor: "#eff6ff",
                  padding: "16px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                  <CheckCircle2 size={48} color="#2D88D4" strokeWidth={1.5} />
                </div>
                <h2 style={{ margin: 0, fontSize: "26px", fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>Registration Successful</h2>
              </div>
              <p style={{ margin: "12px 0 0 0", fontSize: "16px", lineHeight: 1.6, color: "#4b5563" }}>
                Thank you for registering on the <strong>IIIT Bangalore Admissions Portal</strong>.
                Applications for <strong>B.Tech / Integrated M.Tech / M.Tech</strong> will be
                <strong style={{ color: "#ef4444" }}> open soon</strong>.
              </p>
              <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "1px solid #f3f4f6" }}>
                <p style={{ margin: 0, fontSize: "15px", lineHeight: 1.6, color: "#6b7280" }}>
                  We will notify you via email when the application window opens. Once announced, you can
                  log in using the same email and password to apply for your chosen program.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="drives-grid">
            {drives.map((drive) => {
              const isOpen = isDriveOpen(drive.enddate);
              const isDriveEdit = drive?.drivetype === 'Btech' && isDriveEditOpen(drive.editstartdate, drive.editenddate)
              console.log("isDriveEdit", isDriveEdit)
              // Find application for THIS specific drive using drive_id
              const myApplication = applications.find((app: any) => {
                if (app.drive_id !== drive.id) return false;
                // Ignore zombie records: FAILED payment status with no actual data (fullname)
                return !(app.payment_status === "FAILED" && !app.fullname);
              });
              const isApplied = !!myApplication;
              const appStatus = myApplication?.status || null;
              const paymentStatus = myApplication?.payment_status || myApplication?.extra_data?.payment_status;

              const currentOnlineRoundId = myApplication?.online_round5_offered_round_id || myApplication?.online_round5_id || myApplication?.online_round4_offered_round_id || myApplication?.online_round4_id || myApplication?.online_round3_offered_round_id || myApplication?.online_round3_id || myApplication?.online_round2_offered_round_id || myApplication?.online_round2_id || myApplication?.onlineRound;
              const onlineRoundObj = rounds.find((r: any) => r.id === currentOnlineRoundId);
              const isMtech = drives.find((d: any) => d.id === onlineRoundObj?.drive_id)?.drivetype === "Mtech";
              const isOnlineRound2 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 2;
              const isOnlineRound5 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 5;
              const isOnlineRound4 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 4;
                    const isOnlineRound3 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 3;

              const getOnlineField = (app: any, fieldName: 'onlineRound' | 'online_round_status' | 'online_offer_status' | 'online_sliding_status' | 'online_offered_payment_status' | 'online_round_token' | 'online_round_allocated_programme' | 'online_round_offer_letter_doc_id' | 'online_round_name' | 'online_round_allocated_programme_2' | 'online_round_token_2' | 'online_offer_status_2' | 'online_round_offer_letter_doc_id_2') => {
                if (!app) return undefined;
                if (isOnlineRound5) {
                  if (fieldName === 'onlineRound') return app.online_round5_offered_round_id || app.online_round5_id || app.onlineRound;
                  if (fieldName === 'online_round_status') return app.online_round5_status;
                  if (fieldName === 'online_offer_status') return app.online_round5_offer_status;
                  if (fieldName === 'online_sliding_status') return app.online_round5_sliding_status;
                  if (fieldName === 'online_offered_payment_status') return app.online_round5_offered_payment_status;
                  if (fieldName === 'online_round_token') return app.online_round5_token;
                  if (fieldName === 'online_round_allocated_programme') return app.online_round5_allocated_programme;
                  if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round5_offer_letter_doc_id;
                  if (fieldName === 'online_round_allocated_programme_2') return app.online_round5_allocated_programme_2;
                  if (fieldName === 'online_round_token_2') return app.online_round5_token_2;
                  if (fieldName === 'online_offer_status_2') return app.online_round5_offer_status_2;
                  if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round5_offer_letter_doc_id_2;
                  if (fieldName === 'online_round_name') return app.online_round5_name;
                }
                if (isOnlineRound4) {
                  if (fieldName === 'onlineRound') return app.online_round4_offered_round_id || app.online_round4_id || app.onlineRound;
                  if (fieldName === 'online_round_status') return app.online_round4_status;
                  if (fieldName === 'online_offer_status') return app.online_round4_offer_status;
                  if (fieldName === 'online_sliding_status') return app.online_round4_sliding_status;
                  if (fieldName === 'online_offered_payment_status') return app.online_round4_offered_payment_status;
                  if (fieldName === 'online_round_token') return app.online_round4_token;
                  if (fieldName === 'online_round_allocated_programme') return app.online_round4_allocated_programme;
                  if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round4_offer_letter_doc_id;
                  if (fieldName === 'online_round_name') return app.online_round4_name;
                }
                if (isOnlineRound3) {
                  if (fieldName === 'onlineRound') return app.online_round3_offered_round_id || app.online_round3_id || app.onlineRound;
                  if (fieldName === 'online_round_status') return app.online_round3_status;
                  if (fieldName === 'online_offer_status') return app.online_round3_offer_status;
                  if (fieldName === 'online_sliding_status') return app.online_round3_sliding_status;
                  if (fieldName === 'online_offered_payment_status') return app.online_round3_offered_payment_status;
                  if (fieldName === 'online_round_token') return app.online_round3_token;
                  if (fieldName === 'online_round_allocated_programme') return app.online_round3_allocated_programme;
                  if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round3_offer_letter_doc_id;
                  if (fieldName === 'online_round_allocated_programme_2') return app.online_round3_allocated_programme_2;
                  if (fieldName === 'online_round_token_2') return app.online_round3_token_2;
                  if (fieldName === 'online_offer_status_2') return app.online_round3_offer_status_2;
                  if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round3_offer_letter_doc_id_2;
                  if (fieldName === 'online_round_name') return app.online_round3_name;
                }
                if (isOnlineRound2) {
                  if (fieldName === 'onlineRound') return app.online_round2_offered_round_id || app.online_round2_id || app.onlineRound;
                  if (fieldName === 'online_round_status') return app.online_round2_status;
                  if (fieldName === 'online_offer_status') return app.online_round2_offer_status;
                  if (fieldName === 'online_sliding_status') return app.online_round2_sliding_status;
                  if (fieldName === 'online_offered_payment_status') return app.online_round2_offered_payment_status;
                  if (fieldName === 'online_round_token') return app.online_round2_token;
                  if (fieldName === 'online_round_allocated_programme') return app.online_round2_allocated_programme;
                  if (fieldName === 'online_round_offer_letter_doc_id') return app.online_round2_offer_letter_doc_id;
                  if (fieldName === 'online_round_allocated_programme_2') return app.online_round2_allocated_programme_2;
                  if (fieldName === 'online_round_token_2') return app.online_round2_token_2;
                  if (fieldName === 'online_offer_status_2') return app.online_round2_offer_status_2;
                  if (fieldName === 'online_round_offer_letter_doc_id_2') return app.online_round2_offer_letter_doc_id_2;
                  if (fieldName === 'online_round_name') return app.online_round2_name;
                }
                return app[fieldName];
              };

              const editingAllowed = isEditingAllowed(drive.alloweditingenddate);

              const isLocked = isApplied && (appStatus === "SUBMITTED" || appStatus === "EDIT_SUBMIT" || (appStatus === "EDITING" && !editingAllowed));
              const isAppEdit = isApplied && (appStatus === "EDITING" && editingAllowed);
              const isAppDraft = isApplied && (appStatus === "DRAFT");

              const isEnabled = (isOpen && !isLocked) || isAppEdit || (isDriveEdit && (isOpen || isLocked))

              // console.log(
              //   "isOpen", isOpen,
              //   "isDriveEdit", isDriveEdit,
              //   "isApplied", isApplied,
              //   "isAppDraft", isAppDraft,
              //   "isAppEdit", isAppEdit,
              //   "isLocked", isLocked,
              //   "myApplication", myApplication?.application_number
              // )
              function getButtonText({
                isOpen,
                isDriveEdit,
                isApplied,
                isAppDraft,
                isAppEdit,
                isLocked,
              }: {
                isOpen: boolean,
                isDriveEdit: boolean,
                isApplied: boolean,
                isAppDraft: boolean,
                isAppEdit: boolean,
                isLocked: boolean,
              }) {

                // Closed states
                if (!isOpen) {
                  if (isAppEdit) return "Edit Application";
                  if (isDriveEdit && isLocked) {
                    return "Add JEE Advanced Result";
                  }

                  return "Closed";
                }

                // Open states
                if (isOpen && !isApplied) {
                  return "Apply Now";
                }

                if (isOpen && (isAppDraft || isAppEdit)) {
                  return "Edit Application";
                }

                if (isLocked) {
                  return isDriveEdit
                    ? "Add JEE Advanced Result"
                    : "Submitted";
                }

                return "Apply Now";
              }

              const buttonText = getButtonText({
                isOpen,
                isDriveEdit,
                isApplied,
                isAppDraft,
                isAppEdit,
                isLocked,
              });

              const handleOnClick = () => {
                if (isApplied) {
                  if (isAppEdit) {
                    handleApply(drive.id, drive.name, isAppEdit, isLocked);
                  }
                  if (isDriveEdit && isLocked) {
                    handleApply(drive.id, drive.name, isAppEdit, isLocked);
                  } else {
                    // If already applied, just handle apply which navigates to /personaldetails
                    handleApply(drive.id, drive.name, isAppEdit, isLocked);
                  }
                } else {
                  setSelectedDriveToApply({ id: drive.id, name: drive.name });
                  setShowConfirmPopup(true);
                }
              }
              return (
                <div key={drive.id} className="drive-card">
                  <div className="drive-card-header">
                    <div className="header-row">
                      <h3 className="drive-name">{drive.name}</h3>
                      <span
                        className={`drive-status ${isOpen ? "status-active" : "status-closed"}`}
                      >
                        {isOpen ? "Open" : "Closed"}
                      </span>
                    </div>
                  </div>
                  <div className="drive-card-body">
                    {/* <div className="drive-details-row">
                                            <span className="drive-label">Start Date:</span>
                                            <span className="drive-value">{formatDate(drive.startdate)}</span>
                                        </div>
                                        <div className="drive-details-row">
                                            <span className="drive-label">End Date:</span>
                                            <span className="drive-value">{formatDate(drive.enddate)}</span>
                                        </div> */}
                    {isApplied && appStatus && (
                      <div className="drive-details-row">
                        <span className="drive-label">Application Status:</span>
                        <span className={`application-status-badge ${getStatusBadgeClass(appStatus, paymentStatus)}`}>
                          {getStatusLabel(appStatus, paymentStatus)}
                        </span>
                      </div>
                    )}
                    {isApplied && myApplication?.application_number && (
                      <div className="drive-details-row">
                        <span className="drive-label">Application No:</span>
                        <span className="drive-value" style={{ fontSize: "12px", fontFamily: "monospace" }}>
                          {myApplication?.application_number}
                        </span>
                      </div>
                    )}
                    {drive.details && (
                      <p className="drive-description">{drive.details}</p>
                    )}
                    {(
                      <div className="drive-details-row">
                        <span className="drive-label">Last Date To {(!isOpen && isDriveEdit) ? "Edit" : "Apply"}:</span>
                        <span >
                          {(!isOpen && isDriveEdit) ? drive?.editenddate?.slice(0, 12) : drive?.enddate?.slice(0, 12)}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="drive-card-footer">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(!isAppEdit && (isLocked || myApplication?.jee_main_verification_status === "JEE_EDITING" || myApplication?.jee_advanced_verification_status === "JEE_EDITING" || myApplication?.status === "JEE_EDITING" || myApplication?.status === "JEE_MAIN_EDITING" || myApplication?.status === "JEE_ADV_EDITING")) && myApplication?.application_number && (
                        <button
                          className="apply-btn"
                          onClick={() => handleDownloadPDF(myApplication?.application_number)}
                          disabled={downloadingApp === myApplication?.application_number}
                          style={{
                            backgroundColor: "#10b981",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            gap: "8px"
                          }}
                        >
                          <Download size={18} />
                          {downloadingApp === myApplication.application_number ? "Generating..." : "Download Application"}
                        </button>
                      )}
                      {["OFFER_SENT", "ACCEPTED", "REJECTED", "WITHDRAWN"].includes(myApplication?.offer_status) && (() => {
                        const isMtech = drive?.drivetype?.toLowerCase() === "mtech";
                        const isOnlineFlow = getOnlineField(myApplication, 'online_round_status') === "ONLINE_ALLOCATED";
                        // Collect all offered slots
                        let slots = [
                          { slot: 1, p: myApplication?.offered_programme_1, s: myApplication?.offer_status_1, d: myApplication?.offer_letter_doc_id_1 },
                          { slot: 2, p: myApplication?.offered_programme_2, s: myApplication?.offer_status_2, d: myApplication?.offer_letter_doc_id_2 },
                          { slot: 3, p: myApplication?.offered_programme_3, s: myApplication?.offer_status_3, d: myApplication?.offer_letter_doc_id_3 }
                        ].filter(s => s.p);

                        if (slots.length === 0 && myApplication?.offered_programme) {
                            slots = [
                                {
                                    slot: 1,
                                    p: myApplication.offered_programme,
                                    s: myApplication.offer_status,
                                    d: myApplication.offer_letter_doc_id || myApplication.online_round5_offer_letter_doc_id || myApplication.online_round4_offer_letter_doc_id || myApplication.online_round3_offer_letter_doc_id || myApplication.online_round2_offer_letter_doc_id || myApplication.online_round_offer_letter_doc_id
                                }
                            ];
                        }

                        const pendingOffers = slots.filter(s => {
                          if (isMtech) {
                            // For MTech multi-slot offers, show OFFER_SENT slots.
                            // To hide expired prev-round offers, only show the slot that matches the latest global offer_letter_doc_id.
                            // (Since candidates get exactly 1 new offer per round, the global doc_id tracks the latest active offer)
                            if (myApplication?.offer_letter_doc_id) {
                              return s.s === "OFFER_SENT" && s.d === myApplication.offer_letter_doc_id;
                            }
                            return s.s === "OFFER_SENT";
                          } else {
                            const isUpgraded = myApplication?.prev_offered_programme || myApplication?.online_upgrade_notification_sent === "YES" || myApplication?.online_round2_upgrade_notification_sent === "YES" || myApplication?.online_round3_upgrade_notification_sent === "YES" || myApplication?.online_round5_upgrade_notification_sent === "YES" || myApplication?.online_round4_upgrade_notification_sent === "YES" || myApplication?.upgrade_notification_sent === "YES" || myApplication?.round_3_upgrade_notification_sent === "YES";
                            if (myApplication?.offered_payment_status === "SUCCESS" && isUpgraded) {
                              return false; // Paid slide offers are finalized, not pending
                            }
                            return s.s === "OFFER_SENT" && s.p !== myApplication?.prev_offered_programme;
                          }
                        });

                        const rejectedOffers = slots.filter(s => s.s === "REJECTED" && s.p !== myApplication?.prev_offered_programme);
                        // For withdrawn: slot is WITHDRAWN or global is WITHDRAWN and slot was ACCEPTED
                        const withdrawnOffers = slots.filter(s => (s.s === "WITHDRAWN" || (myApplication?.offer_status === "WITHDRAWN" && s.s === "ACCEPTED")) && s.p !== myApplication?.prev_offered_programme);

                        const getEffectiveWithdrawalRoundName = () => {
                            if (myApplication?.online_round5_upgrade_notification_sent === "YES") {
                                return rounds.find((r: any) => r.id === myApplication?.online_round5_id)?.name || myApplication?.offered_round_name;
                            }
                            if (myApplication?.online_round4_upgrade_notification_sent === "YES") {
                                return rounds.find((r: any) => r.id === myApplication?.online_round4_id)?.name || myApplication?.offered_round_name;
                            }
                            if (myApplication?.online_round3_upgrade_notification_sent === "YES") {
                                return rounds.find((r: any) => r.id === myApplication?.online_round3_id)?.name || myApplication?.offered_round_name;
                            }
                            if (myApplication?.online_round2_upgrade_notification_sent === "YES") {
                                return rounds.find((r: any) => r.id === myApplication?.online_round2_id)?.name || myApplication?.offered_round_name;
                            }
                            if (myApplication?.online_upgrade_notification_sent === "YES") {
                                return rounds.find((r: any) => r.id === myApplication?.onlineRound)?.name || myApplication?.offered_round_name;
                            }
                            return myApplication?.offered_round_name;
                        };
                        const effectiveWithdrawalRoundName = getEffectiveWithdrawalRoundName();

                        return (
                          <>
                            {!isOnlineFlow && (
                              <>
                                {/* ── Declined Offers ── */}
                                {rejectedOffers.map((o, idx) => (
                                  <div key={`rej-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                    <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', textAlign: 'center' }}>
                                      You have <strong>Declined</strong> the offer for <strong>{o.p?.replace(/_/g, " ")}</strong>.
                                    </div>
                                    {!(isMtech && myApplication?.prev_round_name && myApplication?.offered_payment_status === "SUCCESS") && (
                                      <button
                                        className="apply-btn"
                                        onClick={() => handleDownloadOfferLetter(myApplication, { programme: o.p, doc_id: o.d })}
                                        style={{ backgroundColor: '#3b82f6', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '8px 10px' }}
                                      >
                                        <Download size={16} /> Download Offer Letter ({o.p?.replace(/_/g, " ")})
                                      </button>
                                    )}
                                  </div>
                                ))}

                                {/* ── Withdrawn Offers ── */}
                                {withdrawnOffers.map((o, idx) => (
                                  <div key={`with-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                                    <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', textAlign: 'center' }}>
                                      You have <strong>Withdrawn</strong> your acceptance for <strong>{o.p?.replace(/_/g, " ")}</strong>.
                                    </div>
                                    {!(isMtech && myApplication?.prev_round_name && myApplication?.offered_payment_status === "SUCCESS") && (
                                      <button
                                        className="apply-btn"
                                        onClick={() => handleDownloadOfferLetter(myApplication, { programme: o.p, doc_id: o.d })}
                                        style={{ backgroundColor: '#3b82f6', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', fontSize: '12px', padding: '8px 10px' }}
                                      >
                                        <Download size={16} /> Download Offer Letter ({o.p?.replace(/_/g, " ")})
                                      </button>
                                    )}
                                  </div>
                                ))}

                                {/* ── Accepted Offer Banner ── */}
                                {myApplication?.offer_status === "ACCEPTED" && (() => {
                                  const acceptedOffer = slots.find(o => o.s === "ACCEPTED");
                                  return (
                                    <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                                      <strong>Congratulations!</strong> You have accepted the offer for <strong>{acceptedOffer?.p?.replace(/_/g, " ")}</strong> and successfully paid the fees. Your admission is confirmed, subject to the verification process.
                                    </div>
                                  );
                                })()}

                                {/* ── MTech Paid Upgrade Accepted Banner ── */}
                                {myApplication?.offer_status === "OFFER_SENT" && myApplication?.offered_payment_status === "SUCCESS" && isMtech && (() => {
                                  const acceptedOffer = slots.find(o => o.s === "ACCEPTED");
                                  if (!acceptedOffer) return null;
                                  return (
                                    <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                                      <strong>Congratulations!</strong> You have accepted the offer for <strong>{acceptedOffer?.p?.replace(/_/g, " ")}</strong> in <strong>{decodeURIComponent(myApplication?.prev_round_name || "").trim()}</strong> and successfully paid the fees. Your admission is confirmed, subject to the verification process.
                                    </div>
                                  );
                                })()}

                                {/* ── BTech Paid Slide Upgrade Banner + Withdraw ── */}
                                {myApplication?.offer_status === "OFFER_SENT" && myApplication?.offered_payment_status === "SUCCESS" && (myApplication?.prev_offered_programme || myApplication?.online_upgrade_notification_sent === "YES" || myApplication?.online_round2_upgrade_notification_sent === "YES" || myApplication?.online_round3_upgrade_notification_sent === "YES" || myApplication?.online_round5_upgrade_notification_sent === "YES" || myApplication?.online_round4_upgrade_notification_sent === "YES" || myApplication?.upgrade_notification_sent === "YES" || myApplication?.round_3_upgrade_notification_sent === "YES") && !isMtech && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                                      <strong>Congratulations!</strong> Your offer has been upgraded to <strong>{myApplication?.offered_programme?.replace(/_/g, " ")}</strong>.
                                    </div>
                                    <button
                                      className="apply-btn"
                                      onClick={() => handleWithdrawal(true)}
                                      disabled={!isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date')}
                                      style={{ backgroundColor: !isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? '#9ca3af' : '#f59e0b', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: !isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? 'not-allowed' : 'pointer' }}
                                      title={!isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? "Withdrawal deadline has passed" : ""}
                                    >
                                      Withdraw Acceptance
                                    </button>
                                  </div>
                                )}

                                {/* ── Pending Offers ─────────── */}
                                {pendingOffers.length > 0 && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <p style={{ margin: '0 0 4px 0', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                      {["ACCEPTED", "WITHDRAWN"].includes(myApplication?.offer_status) ? "Other programme offers" : `You have ${pendingOffers.length} pending offer${pendingOffers.length > 1 ? 's' : ''}`}
                                    </p>
                                    {pendingOffers.map((offer: any) => (
                                      <div key={offer.p} style={{
                                        border: '1px solid #e5e7eb', borderRadius: '8px',
                                        padding: '10px 12px', backgroundColor: '#f9fafb'
                                      }}>
                                        <p style={{ margin: '0 0 4px 0', fontWeight: 600, fontSize: '14px' }}>
                                          {offer.p?.replace(/_/g, " ")}
                                        </p>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                          {myApplication?.offered_payment_status !== "SUCCESS" && (
                                            <button
                                              className="apply-btn"
                                              onClick={() => handleDownloadOfferLetter(myApplication, { programme: offer.p, doc_id: offer.d })}
                                              style={{ backgroundColor: '#3b82f6', fontSize: '12px', padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                                            >
                                              <Download size={14} /> Download Offer Letter
                                            </button>
                                          )}
                                          <button
                                            className="apply-btn"
                                            onClick={() => {
                                              setSelectedAppToAccept(myApplication);
                                              setSelectedOfferToAction({ programme: offer.p, slot: offer.slot, round_name: myApplication?.offered_round_name });
                                              setShowAcceptOfferPopup(true);
                                            }}
                                            disabled={!isActionAllowed(myApplication?.offered_round_name, 'enddate')}
                                            style={{ backgroundColor: !isActionAllowed(myApplication?.offered_round_name, 'enddate') ? '#9ca3af' : '#3b82f6', fontSize: '12px', padding: '8px 10px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: !isActionAllowed(myApplication?.offered_round_name, 'enddate') ? 'not-allowed' : 'pointer' }}
                                            title={!isActionAllowed(myApplication?.offered_round_name, 'enddate') ? "Accept/Decline deadline has passed" : ""}
                                          >
                                            <CheckCircle2 size={14} /> Accept/Decline Offer
                                          </button>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* ── ACCEPTED: download + withdraw ─────────────── */}
                                {myApplication?.offer_status === "ACCEPTED" && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {!(isMtech && myApplication?.prev_round_name && myApplication?.offered_payment_status === "SUCCESS") && (() => {
                                      const acceptedOffer = slots.find(o => o.s === "ACCEPTED");
                                      return (
                                        <button
                                          className="apply-btn"
                                          onClick={() => handleDownloadOfferLetter(myApplication, { programme: acceptedOffer?.p, doc_id: acceptedOffer?.d })}
                                          style={{ backgroundColor: '#3b82f6', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                                        >
                                          <Download size={18} /> Download Offer Letter
                                        </button>
                                      );
                                    })()}
                                    <button
                                      className="apply-btn"
                                      onClick={() => handleWithdrawal(true)}
                                      disabled={!isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date')}
                                      style={{ backgroundColor: !isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? '#9ca3af' : '#f59e0b', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: !isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? 'not-allowed' : 'pointer' }}
                                      title={!isActionAllowed(effectiveWithdrawalRoundName, 'withdrawal_end_date') ? "Withdrawal deadline has passed" : ""}
                                    >
                                      Withdraw Acceptance
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </>
                        );
                      })()}
                      {(myApplication?.jee_main_verification_status === "JEE_EDITING" ||
                        myApplication?.jee_advanced_verification_status === "JEE_EDITING" ||
                        myApplication?.status === "JEE_EDITING" ||
                        myApplication?.status === "JEE_MAIN_EDITING" ||
                        myApplication?.status === "JEE_ADV_EDITING"
                      ) && isEditingAllowed(drive.jee_editing_end_date) &&
                        (<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <button
                            className="apply-btn"
                            onClick={() => handleJeeUpdate(true)}
                            style={{ backgroundColor: '#f59e0b', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                          >
                            Update JEE Details
                          </button>
                        </div>)
                      }

                      {/* ── Online Round Registration Banners ── */}
                      {getOnlineField(myApplication, 'online_round_status') === "INVITED" && (() => {
                        const onlineRound = rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'));
                        let isPastDeadline = false;
                        let deadlineStr = "";

                        if (onlineRound?.registration_end_date) {
                          const parsedDate = parseSafeDate(onlineRound.registration_end_date);
                          if (parsedDate && !isNaN(parsedDate.getTime())) {
                            // Set deadline to 23:59:59 of the given date
                            const deadlineTime = new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate(), 23, 59, 59).getTime();
                            const currentTime = new Date().getTime() + serverTimeOffset;
                            isPastDeadline = currentTime > deadlineTime;
                          }
                        }

                        if (isPastDeadline) return null;

                        return (
                          <div style={{ border: '1px solid #93c5fd', borderRadius: '8px', padding: '12px', backgroundColor: '#eff6ff', marginBottom: '12px' }}>
                            <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#1e3a8a' }}>
                              You have been invited to register for {getOnlineField(myApplication, 'online_round_name') || "the Online Admission Round"}.
                            </p>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                className="apply-btn"
                                disabled={isPastDeadline}
                                title={isPastDeadline ? "The registration deadline has passed." : ""}
                                onClick={() => {
                                  if (isPastDeadline) return;
                                  setOnlineRegApp(myApplication);
                                  setOnlineRegAction('REGISTER');
                                  setShowOnlineRegPopup(true);
                                }}
                                style={{
                                  flex: 1, backgroundColor: '#3b82f6', fontSize: '12px', padding: '8px', display: 'flex', justifyContent: 'center',
                                  opacity: isPastDeadline ? 0.6 : 1, cursor: isPastDeadline ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Register
                              </button>
                              <button
                                className="apply-btn"
                                disabled={isPastDeadline}
                                title={isPastDeadline ? "The registration deadline has passed." : ""}
                                onClick={() => {
                                  if (isPastDeadline) return;
                                  setOnlineRegApp(myApplication);
                                  setOnlineRegAction('DECLINE');
                                  setShowOnlineRegPopup(true);
                                }}
                                style={{
                                  flex: 1, backgroundColor: '#ef4444', fontSize: '12px', padding: '8px', display: 'flex', justifyContent: 'center',
                                  opacity: isPastDeadline ? 0.6 : 1, cursor: isPastDeadline ? 'not-allowed' : 'pointer'
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          </div>
                        );
                      })()}

                      {getOnlineField(myApplication, 'online_round_status') === "ONLINE_REGISTERED" && (
                        <div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', textAlign: 'center' }}>
                          You have <strong>registered</strong> for {getOnlineField(myApplication, 'online_round_name') || "the Online Round"}. Please wait for token generation and further instructions.
                        </div>
                      )}

                      {getOnlineField(myApplication, 'online_round_status') === "ONLINE_DECLINED" && (
                        <div className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', textAlign: 'center' }}>
                          You have <strong>declined</strong> the registration for {getOnlineField(myApplication, 'online_round_name') || "the Online Round"}.
                        </div>
                      )}

                      {["TOKEN_GENERATED", "TOKEN_EMAIL_SENT"].includes(getOnlineField(myApplication, 'online_round_status') || "") && (
                        <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                          Your {getOnlineField(myApplication, 'online_round_name') || "Online Round"} token has been generated: <strong>{getOnlineField(myApplication, 'online_round_token')}</strong>. Please check your email for the joining link and instructions.
                        </div>
                      )}

                      {myApplication?.offer_status === "WITHDRAWN" && getOnlineField(myApplication, 'online_round_status') === "ONLINE_ALLOCATED" && (
                        <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', color: '#92400e', textAlign: 'center' }}>
                          You have <strong>Withdrawn</strong> your acceptance for <strong>{getOnlineField(myApplication, 'online_round_allocated_programme')?.replace(/_/g, " ")}</strong>.
                        </div>
                      )}

                      {(() => {
                        const offerStatus1 = getOnlineField(myApplication, 'online_offer_status');
                        const offerStatus2 = getOnlineField(myApplication, 'online_offer_status_2');
                        if (myApplication?.offer_status === "WITHDRAWN") return null;

                        const onlineOffers = [
                          {
                            p: getOnlineField(myApplication, 'online_round_allocated_programme'),
                            s: offerStatus1,
                            d: getOnlineField(myApplication, 'online_round_offer_letter_doc_id')
                          },
                          {
                            p: getOnlineField(myApplication, 'online_round_allocated_programme_2'),
                            s: offerStatus2,
                            d: getOnlineField(myApplication, 'online_round_offer_letter_doc_id_2')
                          }
                        ].filter(x => x.p && x.s === "OFFER_SENT");

                        if (onlineOffers.length === 0) return null;

                        const isPaidUpgrade = getOnlineField(myApplication, 'online_offered_payment_status') === "SUCCESS" || myApplication?.offered_payment_processed === true || myApplication?.offered_payment_status === "SUCCESS";
                        if (isPaidUpgrade) {
                          const isMtechApp = drive?.drivetype === "Mtech";
                          if (isMtechApp) return null;
                          const prog = getOnlineField(myApplication, 'online_round_allocated_programme')?.replace(/_/g, " ");
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                                <strong>Congratulations!</strong> Your offer has been upgraded to <strong>{prog}</strong>.
                              </div>
                              <button
                                className="apply-btn"
                                onClick={() => handleWithdrawal(true)}
                                disabled={!isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date')}
                                style={{ backgroundColor: !isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? '#9ca3af' : '#f59e0b', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: !isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? 'not-allowed' : 'pointer' }}
                                title={!isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? "Withdrawal deadline has passed" : ""}
                              >
                                Withdraw Acceptance
                              </button>
                            </div>
                          );
                        }

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '12px' }}>
                            {onlineOffers.map((offer, idx) => (
                              <div key={idx} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', backgroundColor: '#f9fafb' }}>
                                <p style={{ margin: '0 0 8px 0', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                                  You have a pending offer for <strong>{offer.p?.replace(/_/g, " ")}</strong>.
                                </p>
                                <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
                                  {offer.d && (
                                    <button
                                      className="apply-btn"
                                      onClick={() => handleDownloadOfferLetter(myApplication, { doc_id: offer.d })}
                                      style={{ backgroundColor: '#3b82f6', fontSize: '12px', padding: '8px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                                    >
                                      <Download size={14} /> Download Offer Letter
                                    </button>
                                  )}
                                  <button
                                    className="apply-btn"
                                    onClick={() => {
                                      setSelectedAppToAccept(myApplication);
                                      setSelectedOfferToAction({
                                        programme: offer.p,
                                        isOnline: true,
                                        isOnlineRound2: isOnlineRound2,
                                        isOnlineRound5: isOnlineRound5,
                                        isOnlineRound4: isOnlineRound4,
                                        isOnlineRound3: isOnlineRound3,
                                        round_name: rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "Online Round"
                                      });
                                      setShowAcceptOfferPopup(true);
                                    }}
                                    style={{ backgroundColor: '#10b981', fontSize: '12px', padding: '8px', display: 'flex', justifyContent: 'center' }}
                                  >
                                    Accept / Decline Offer
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}

                      {(() => {
                        const offerStatus1 = getOnlineField(myApplication, 'online_offer_status');
                        const offerStatus2 = getOnlineField(myApplication, 'online_offer_status_2');
                        
                        const rejectedOffers = [
                          { p: getOnlineField(myApplication, 'online_round_allocated_programme'), s: offerStatus1 },
                          { p: getOnlineField(myApplication, 'online_round_allocated_programme_2'), s: offerStatus2 }
                        ].filter(x => x.p && x.s === "REJECTED");

                        return rejectedOffers.map((offer, idx) => (
                          <div key={`rej-${idx}`} className="alert alert-danger py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', textAlign: 'center' }}>
                            You have <strong>Declined</strong> the offer for <strong>{offer.p?.replace(/_/g, " ")}</strong> in {getOnlineField(myApplication, 'online_round_name') || "the Online Round"}.
                          </div>
                        ));
                      })()}

                      {(() => {
                        const offerStatus1 = getOnlineField(myApplication, 'online_offer_status');
                        const offerStatus2 = getOnlineField(myApplication, 'online_offer_status_2');
                        
                        const acceptedOffers = [
                          { p: getOnlineField(myApplication, 'online_round_allocated_programme'), s: offerStatus1 },
                          { p: getOnlineField(myApplication, 'online_round_allocated_programme_2'), s: offerStatus2 }
                        ].filter(x => x.p && x.s === "ACCEPTED");

                        if (myApplication?.offer_status === "WITHDRAWN" || acceptedOffers.length === 0) return null;

                        return acceptedOffers.map((offer, idx) => (
                          <div key={`acc-${idx}`} style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
                            <div className="alert alert-success py-2 px-3 mb-2" style={{ fontSize: '13px', borderRadius: '8px', backgroundColor: '#d1fae5', border: '1px solid #a7f3d0', color: '#065f46', textAlign: 'center' }}>
                              <strong>Congratulations!</strong> You have accepted the offer for <strong>{offer.p?.replace(/_/g, " ")}</strong> and successfully paid the fees. Your admission is confirmed, subject to the verification process.
                            </div>
                            <button
                              className="apply-btn"
                              onClick={() => handleWithdrawal(true)}
                              disabled={!isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date')}
                              style={{ backgroundColor: !isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? '#9ca3af' : '#f59e0b', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: !isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? 'not-allowed' : 'pointer' }}
                              title={!isActionAllowed(rounds.find((r: any) => r.id === getOnlineField(myApplication, 'onlineRound'))?.name || "", 'withdrawal_end_date') ? "Withdrawal deadline has passed" : ""}
                            >
                              Withdraw Acceptance
                            </button>
                          </div>
                        ));
                      })()}

                      <button
                        className="apply-btn"
                        disabled={!isEnabled}
                        onClick={handleOnClick}
                      >
                        {buttonText}
                      </button>

                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Confirmation Popup */}
      {showConfirmPopup && selectedDriveToApply && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.25rem', color: '#111827', fontWeight: 600 }}>Confirm Application</h3>
            <div style={{ marginBottom: '24px', color: '#374151', lineHeight: '1.6', fontSize: '16px' }}>
              <p style={{ marginBottom: '8px', marginTop: 0 }}>
                Are you sure you want to apply for
              </p>
              <p style={{ marginBottom: '16px', marginTop: 0, fontWeight: 600, color: '#111827', fontSize: '17px' }}>
                {getProgrammeName(selectedDriveToApply?.name || "")}
              </p>
              {selectedDriveToApply?.name?.toLowerCase().includes('m.tech') &&
                !selectedDriveToApply?.name?.toLowerCase().includes('b.tech') &&
                !selectedDriveToApply?.name?.toLowerCase().includes('i.m') &&
                !selectedDriveToApply?.name?.toLowerCase().includes('integrated') && (
                  <ul style={{ marginTop: 0, marginBottom: 0, paddingLeft: '24px', color: '#4b5563' }}>
                    <li style={{ marginBottom: '6px' }}>Computer Science & Engineering (CSE)</li>
                    <li style={{ marginBottom: '6px' }}>Electronics & Communication Engineering (ECE)</li>
                    <li>Artificial Intelligence & Data Science (AI & DS)</li>
                  </ul>
                )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => setShowConfirmPopup(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#e5e7eb'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (selectedDriveToApply) {
                    handleApply(selectedDriveToApply.id, selectedDriveToApply.name, false, false);
                    setShowConfirmPopup(false);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#0056b3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#004494'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#0056b3'}
              >
                Apply Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Accept Offer Confirmation Popup */}
      {showAcceptOfferPopup && selectedAppToAccept && (offerActionConfirm === null || offerActionConfirm === "REJECT" || offerActionConfirm === "ACCEPT_CONFIRM") && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1001
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.25rem', color: '#111827', fontWeight: 600 }}>
              {offerActionConfirm === 'REJECT' || offerActionConfirm === 'ACCEPT_CONFIRM' ? 'Confirm Action' : 'Action Required'}
            </h3>
            <div style={{ marginBottom: '24px', color: '#374151', lineHeight: '1.6', fontSize: '16px' }}>
              {offerActionConfirm === 'REJECT' ? (
                <p>Are you sure you want to <strong>DECLINE</strong> the offer for <strong>{(selectedOfferToAction?.programme || selectedAppToAccept?.offered_programme)?.replace(/_/g, " ")}</strong> for <strong>{decodeURIComponent(selectedOfferToAction?.round_name || selectedAppToAccept?.offered_round_name || "").trim()}</strong>? This action is final and cannot be undone.</p>
              ) : offerActionConfirm === 'ACCEPT_CONFIRM' ? (
                <p>Are you sure you want to <strong>ACCEPT</strong> the offer for <strong>{(selectedOfferToAction?.programme || selectedAppToAccept?.offered_programme)?.replace(/_/g, " ")}</strong> for <strong>{decodeURIComponent(selectedOfferToAction?.round_name || selectedAppToAccept?.offered_round_name || "").trim()}</strong>? This will confirm your admission for this programme.</p>
              ) : (
                <>
                  <p>Please review and choose an action for this offer:</p>
                  {(selectedOfferToAction || selectedAppToAccept?.offered_programme) && (
                    <div style={{ backgroundColor: '#f9fafb', padding: '12px', borderRadius: '8px', border: '1px solid #f3f4f6', marginTop: '12px' }}>
                      <p style={{ margin: 0 }}>Programme: <strong>
                        {(selectedOfferToAction?.programme || selectedAppToAccept?.offered_programme)?.replace(/_/g, " ")}
                      </strong></p>
                      {(selectedOfferToAction?.round_name || selectedAppToAccept?.offered_round_name) && (
                        <p style={{ margin: '4px 0 0 0' }}>Round: <strong>
                          {decodeURIComponent(selectedOfferToAction?.round_name || selectedAppToAccept?.offered_round_name || "").trim()}
                        </strong></p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  if (offerActionConfirm) {
                    setOfferActionConfirm(null);
                  } else {
                    setShowAcceptOfferPopup(false);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                {offerActionConfirm ? 'Back' : 'Close'}
              </button>
              {!offerActionConfirm && (
                <>
                  <button
                    onClick={() => setOfferActionConfirm('REJECT')}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    Decline Offer
                  </button>
                  <button
                    onClick={() => {
                      // Online offers always go straight to payment (no prev-paid check needed)
                      if (!selectedOfferToAction?.isOnline && selectedAppToAccept?.offered_payment_status === "SUCCESS") {
                        setOfferActionConfirm('ACCEPT_CONFIRM');
                      } else {
                        setOfferActionConfirm('ACCEPT');
                      }
                    }}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: '#54ad58',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                  >
                    {(!selectedOfferToAction?.isOnline && selectedAppToAccept?.offered_payment_status === "SUCCESS") ? "Accept Offer" : "Accept Offer & Pay"}
                  </button>
                </>
              )}
              {offerActionConfirm === 'REJECT' && (
                <button
                  onClick={() => handleRejectOffer(selectedAppToAccept)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Confirm Decline
                </button>
              )}
              {offerActionConfirm === 'ACCEPT_CONFIRM' && (
                <button
                  onClick={() => handleAcceptOffer(selectedAppToAccept)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#54ad58',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  Confirm Accept
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Withdraw Acceptance Confirmation Popup */}
      {showWithdrawConfirmPopup && selectedAppToWithdraw && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1002
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.25rem', color: '#111827', fontWeight: 600 }}>Confirm Withdrawal</h3>
            <div style={{ marginBottom: '24px', color: '#374151', lineHeight: '1.6', fontSize: '16px' }}>
              <p>Are you sure you want to withdraw your acceptance for <strong>{getAcceptedProgrammeName(selectedAppToWithdraw)}</strong> in <strong>{decodeURIComponent(selectedAppToWithdraw?.offered_round_name || "").trim()}</strong>? <strong>This action cannot be undone.</strong></p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowWithdrawConfirmPopup(false);
                  setSelectedAppToWithdraw(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Close
              </button>
              <button
                onClick={async () => {
                  await handleUpdateAppStatus(selectedAppToWithdraw, "WITHDRAWN", `You have withdrawn your acceptance for ${getAcceptedProgrammeName(selectedAppToWithdraw)}`);
                  setShowWithdrawConfirmPopup(false);
                  setSelectedAppToWithdraw(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f59e0b',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Withdraw Acceptance
              </button>
            </div>
          </div>
        </div>
      )}

      {typeof (effectiveRoundFee) === "number" && selectedAppToAccept && offerActionConfirm === 'ACCEPT' &&
        <PaymentIciciInitiate
          programme={selectedOfferToAction?.programme || selectedAppToAccept?.offered_programme}
          roundName={effectiveRoundName}
          roundFee={effectiveRoundFee}
          slot={selectedOfferToAction?.slot}
          selectedAppToAccept={selectedAppToAccept}
          onClose={() => {
            setOfferActionConfirm(null);
            setSelectedAppToAccept(null);
            setSelectedOfferToAction(null);
            setShowAcceptOfferPopup(false);
          }}
        />}

      {/* <HelpQueryPopup
        show={showHelpPopup}
        onClose={() => setShowHelpPopup(false)}
        openMyQueries={() => {
          setShowHelpPopup(false);
          setShowMyQueriesPopup(true);
        }}
      />  */}


      {/* <MyQueriesPopup
        isOpen={showMyQueriesPopup}
        onClose={() => setShowMyQueriesPopup(false)}
        openQueryChat={(query) => {
          if (query) {
            setSelectedQueryId(query.id);
            setShowMyQueriesPopup(false);
            setShowQueryChatPopup(true);
            setSelectedQuery(query);
          } else {
            // "Raise New Query" was clicked
            setShowMyQueriesPopup(false);
            setShowHelpPopup(true);
          }
        }}
      /> */}

      {/* {selectedQuery && (
        <QueryChatPopup
          isOpen={showQueryChatPopup}
          onClose={() => {
            setShowQueryChatPopup(false);
            setSelectedQueryId("");
            setSelectedQuerySubject("");
            setSelectedQuery(null);
          }}
          onBack={() => {
            setShowQueryChatPopup(false);
            setSelectedQueryId("");
            setSelectedQuerySubject("");
            setSelectedQuery(null);
            setShowMyQueriesPopup(true);
          }}
          queryId={selectedQueryId}
          querySubject={selectedQuerySubject}
          query={selectedQuery}
        />
      )} */}

      {showOnlineRegPopup && onlineRegApp && onlineRegAction && (
        <div className="modal-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div className="modal-content" style={{
            backgroundColor: '#fff',
            padding: '24px',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
            fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif'
          }}>
            <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '1.25rem', color: '#111827', fontWeight: 600 }}>Confirm Action</h3>
            <div style={{ marginBottom: '24px', color: '#374151', lineHeight: '1.6', fontSize: '16px' }}>
              {(() => {
                const appDrive = drives.find((d: any) => d.id === onlineRegApp.drive_id);
                const driveTypeName = appDrive?.drivetype === "Mtech" ? "M.Tech" : appDrive?.drivetype === "Btech" ? "B.Tech-Integrated M.Tech" : "";
                const currentOnlineRoundId = onlineRegApp?.online_round5_offered_round_id || onlineRegApp?.online_round5_id || onlineRegApp?.online_round4_offered_round_id || onlineRegApp?.online_round4_id || onlineRegApp?.online_round3_offered_round_id || onlineRegApp?.online_round3_id || onlineRegApp?.online_round2_offered_round_id || onlineRegApp?.online_round2_id || onlineRegApp?.onlineRound;
                const onlineRoundObj = rounds.find((r: any) => r.id === currentOnlineRoundId);
                const isRound3 = onlineRoundObj?.round_number === 3 || currentOnlineRoundId === onlineRegApp?.online_round3_offered_round_id || currentOnlineRoundId === onlineRegApp?.online_round3_id;
                const isRound2 = onlineRoundObj?.round_number === 2 || currentOnlineRoundId === onlineRegApp?.online_round2_offered_round_id || currentOnlineRoundId === onlineRegApp?.online_round2_id;
                
                const fallbackName = isRound3 ? onlineRegApp?.online_round3_name : (isRound2 ? onlineRegApp?.online_round2_name : onlineRegApp?.online_round_name);
                const roundName = onlineRoundObj?.name || fallbackName || "Online Admission Round";

                const roundNameStr = String(roundName);
                const displayRoundName = roundNameStr.toLowerCase().includes(driveTypeName.toLowerCase().replace(/[\s.-]/g, '')) 
                    || roundNameStr.toLowerCase().includes("m.tech")
                    || roundNameStr.toLowerCase().includes("b.tech")
                  ? roundNameStr 
                  : `${driveTypeName ? `${driveTypeName} ` : ''}${roundNameStr}`;

                return (
                  <p style={{ marginBottom: '8px', marginTop: 0 }}>
                    Are you sure you want to <strong>{onlineRegAction}</strong> for the <strong>{displayRoundName}</strong>?
                  </p>
                );
              })()}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={() => {
                  setShowOnlineRegPopup(false);
                  setOnlineRegApp(null);
                  setOnlineRegAction(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f3f4f6',
                  color: '#4b5563',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
              <button
                onClick={async () => {
                  const status = onlineRegAction === 'REGISTER' ? 'ONLINE_REGISTERED' : 'ONLINE_DECLINED';
                  try {
                    const currentOnlineRoundId = onlineRegApp?.online_round5_offered_round_id || onlineRegApp?.online_round5_id || onlineRegApp?.online_round4_offered_round_id || onlineRegApp?.online_round4_id || onlineRegApp?.online_round3_offered_round_id || onlineRegApp?.online_round3_id || onlineRegApp?.online_round2_offered_round_id || onlineRegApp?.online_round2_id || onlineRegApp?.onlineRound;
                    const onlineRoundObj = rounds.find((r: any) => r.id === currentOnlineRoundId);
                    const isMtech = drives.find((d: any) => d.id === onlineRoundObj?.drive_id)?.drivetype === "Mtech";
                    const isOnlineRound2 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 2;
                    const isOnlineRound5 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 5;
              const isOnlineRound4 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 4;
                    const isOnlineRound3 = onlineRoundObj?.round_type === "Online" && onlineRoundObj?.round_number === 3;

                    const url = `${apiConfig.getResourceUrl("applications")}?queryId=UPDATE_ONLINE_PARTICIPATION&args=${encodeURIComponent(`application_id:${onlineRegApp.id},status:${status},is_round5:${isOnlineRound5},is_round4:${isOnlineRound4},is_round3:${isOnlineRound3},is_round2:${isOnlineRound2}`)}`;
                    const response = await apiFetch(url, { method: "GET" });
                    const res = await response.json();
                    if (res?.errCode === 0) {
                      toast.success(onlineRegAction === 'REGISTER' ? "Registration accepted successfully." : "Registration declined.");
                      setApplications(prev => prev.map(a => a.id === onlineRegApp.id ? { ...a, [isOnlineRound5 ? "online_round5_status" : (isOnlineRound4 ? "online_round4_status" : (isOnlineRound3 ? "online_round3_status" : (isOnlineRound2 ? "online_round2_status" : "online_round_status")))]: status } : a));
                    } else toast.error(res?.message || "Failed to update registration.");
                  } catch (err) { toast.error("An error occurred."); }
                  setShowOnlineRegPopup(false);
                  setOnlineRegApp(null);
                  setOnlineRegAction(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: onlineRegAction === 'REGISTER' ? '#3b82f6' : '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Confirm {onlineRegAction}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="student-footer">
        <div className="footer-social">
          <a href="https://www.facebook.com/IIITBofficial">
            <Facebook size={20} />
          </a>
          <a href="https://x.com/IIITB_official">
            <Twitter size={20} />
          </a>
          <a href="https://www.linkedin.com/school/iiitbofficial">
            <Linkedin size={20} />
          </a>
          <a href="https://www.youtube.com/user/iiitbmedia">
            <Youtube size={20} />
          </a>
        </div>
        <div className="footer-copyright">
          <p className="footer-text">
            © 2026 International Institute of Information Technology - Bangalore
          </p>
          <p className="footer-support">
            Support - admissions2026@iiitb.ac.in | +91 80 4140 7777 (9:00 AM –
            5:00 PM)
          </p>
        </div>
      </footer>
    </div>
  );
};

export default StudentHome;


